import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { publicAnalyticsRepository } from '../repositories/publicAnalytics.repository';
import { analyticsKeys } from '../services/analytics.keys';
import type { CampaignPerformanceDataPoint, PlatformDistributionItem } from '../types/analytics.types';
import type {
  PublicCampaignMetadata,
  PublicCampaignSummary,
  PublicDeliverableAggregate,
  PublicCampaignBranding,
} from '../types/sharedAnalytics.types';
import type { TopPost } from '@/hooks/useTopPerformingPosts';

const PUBLIC_REFETCH_INTERVAL = 30_000;

export type {
  PublicCampaignMetadata,
  PublicDeliverableAggregate,
  PublicCampaignBranding,
};

export const usePublicCampaignMetadata = (token: string | undefined) => {
  return useQuery<PublicCampaignMetadata | null, Error>({
    queryKey: analyticsKeys.public.metadata(token || ''),
    queryFn: () => {
      if (!token) throw new Error('No share token provided');
      return publicAnalyticsRepository.getSharedCampaignMetadata(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error?.message?.includes('not found') || error?.message?.includes('invalid')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const usePublicCampaignBranding = (token: string | undefined) => {
  return useQuery<PublicCampaignBranding | null, Error>({
    queryKey: analyticsKeys.public.branding(token || ''),
    queryFn: () => {
      if (!token) return null;
      return publicAnalyticsRepository.getSharedCampaignBranding(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error?.message?.includes('not found') || error?.message?.includes('invalid')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const usePublicCampaignPerformanceTimeSeries = (token: string | undefined) => {
  return useQuery<CampaignPerformanceDataPoint[], Error>({
    queryKey: analyticsKeys.public.timeSeries(token || ''),
    queryFn: () => {
      if (!token) return [];
      return publicAnalyticsRepository.getSharedCampaignPerformanceTimeSeries(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
  });
};

export const usePublicCampaignSummary = (token: string | undefined) => {
  return useQuery<PublicCampaignSummary | null, Error>({
    queryKey: analyticsKeys.public.summary(token || ''),
    queryFn: () => {
      if (!token) return null;
      return publicAnalyticsRepository.getSharedCampaignSummary(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
  });
};

export const usePublicDeliverableAggregates = (token: string | undefined) => {
  return useQuery<PublicDeliverableAggregate[], Error>({
    queryKey: analyticsKeys.public.aggregates(token || ''),
    queryFn: () => {
      if (!token) return [];
      return publicAnalyticsRepository.getSharedDeliverableAggregates(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
  });
};

export const usePublicPlatformDistribution = (token: string | undefined) => {
  return useQuery<PlatformDistributionItem[], Error>({
    queryKey: analyticsKeys.public.platformDistribution(token || ''),
    queryFn: () => {
      if (!token) return [];
      return publicAnalyticsRepository.getSharedPlatformDistribution(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
  });
};

export const usePublicTopPerformingPosts = (token: string | undefined) => {
  return useQuery<TopPost[], Error>({
    queryKey: analyticsKeys.public.topPosts(token || ''),
    queryFn: () => {
      if (!token) return [];
      return publicAnalyticsRepository.getSharedTopPerformingPosts(token);
    },
    enabled: !!token,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: PUBLIC_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
  });
};

export interface PublicCampaignBrand {
  id?: string;
  name?: string;
  logo_url?: string | null;
}

export const usePublicCampaignBrand = (campaignId: string | undefined) => {
  return useQuery<PublicCampaignBrand | null, Error>({
    queryKey: analyticsKeys.public.brand(campaignId || ''),
    queryFn: async () => {
      if (!campaignId) return null;
      try {
        const { data, error } = await supabase
          .from('campaigns')
          .select(`
            brand_id,
            brands (
              id,
              name,
              logo_url
            )
          `)
          .eq('id', campaignId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching campaign brand:', error);
          return null;
        }

        if (!data?.brands) return null;
        const brand = Array.isArray(data.brands) ? data.brands[0] : data.brands;
        return (brand as unknown as PublicCampaignBrand) || null;
      } catch (err) {
        console.error('Error in usePublicCampaignBrand:', err);
        return null;
      }
    },
    enabled: !!campaignId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

export interface PublicCampaignOrganization {
  id?: string;
  name?: string;
  logo_url?: string | null;
  organization_type?: string | null;
}

export const usePublicCampaignOrganization = (
  organizationId: string | undefined,
  campaignId?: string | undefined
) => {
  const queryKeyId = organizationId || campaignId || '';
  return useQuery<PublicCampaignOrganization | null, Error>({
    queryKey: analyticsKeys.public.organization(queryKeyId),
    queryFn: async () => {
      if (!organizationId && !campaignId) return null;

      try {
        // 1. Try querying via campaigns relation
        if (campaignId) {
          const { data: campData, error: campError } = await supabase
            .from('campaigns')
            .select(`
              organization_id,
              organizations (
                id,
                name,
                organization_type,
                settings
              )
            `)
            .eq('id', campaignId)
            .maybeSingle();

          if (!campError && campData?.organizations) {
            const org = Array.isArray(campData.organizations)
              ? campData.organizations[0]
              : campData.organizations;
            if (org && org.name) {
              const settings =
                org.settings && typeof org.settings === 'object'
                  ? (org.settings as Record<string, unknown>)
                  : {};
              const logoUrl =
                (settings.logo_url as string) ||
                (settings.logo as string) ||
                ((org as Record<string, unknown>).logo_url as string) ||
                null;

              if (logoUrl) {
                return {
                  id: org.id,
                  name: org.name,
                  organization_type: org.organization_type,
                  logo_url: logoUrl,
                };
              }

              // Fallback for agency logo if not directly on settings
              const { data: agencyData } = await (supabase as any)
                .from('agencies')
                .select('logo_url')
                .order('created_at', { ascending: true })
                .limit(1)
                .maybeSingle();

              return {
                id: org.id,
                name: org.name,
                organization_type: org.organization_type,
                logo_url: (agencyData?.logo_url as string) || null,
              };
            }
          }
        }

        // 2. Direct organization lookup by organizationId
        const targetOrgId = organizationId;
        if (targetOrgId) {
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('id, name, organization_type, settings')
            .eq('id', targetOrgId)
            .maybeSingle();

          if (!orgError && orgData && orgData.name) {
            const settings =
              orgData.settings && typeof orgData.settings === 'object'
                ? (orgData.settings as Record<string, unknown>)
                : {};
            const logoUrl =
              (settings.logo_url as string) ||
              (settings.logo as string) ||
              ((orgData as Record<string, unknown>).logo_url as string) ||
              null;

            if (logoUrl) {
              return {
                id: orgData.id,
                name: orgData.name,
                organization_type: orgData.organization_type,
                logo_url: logoUrl,
              };
            }

            const { data: agencyData } = await (supabase as any)
              .from('agencies')
              .select('logo_url')
              .order('created_at', { ascending: true })
              .limit(1)
              .maybeSingle();

            return {
              id: orgData.id,
              name: orgData.name,
              organization_type: orgData.organization_type,
              logo_url: (agencyData?.logo_url as string) || null,
            };
          }
        }

        // 3. Fallback to agencies table (stored by GeneralSettings)
        const { data: agencyData, error: agencyError } = await (supabase as any)
          .from('agencies')
          .select('id, name, logo_url')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (!agencyError && agencyData && (agencyData.name || agencyData.logo_url)) {
          return {
            id: agencyData.id,
            name: agencyData.name,
            organization_type: 'agency',
            logo_url: (agencyData.logo_url as string) || null,
          };
        }

        return null;
      } catch (err) {
        console.error('Error in usePublicCampaignOrganization:', err);
        return null;
      }
    },
    enabled: !!organizationId || !!campaignId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};


